// src/pages/quotation/CreateQuotationPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Save, FileText, X, Store, User, Package, Search, Hash, AlertCircle, Minus, Plus,
} from 'lucide-react';
import { createQuotation } from '../../services/api/offlineSales/quotations';
import { getShops } from '../../services/api/shops';
import { getCustomers } from '../../services/api/offlineSales/customers';
import useProductSearch from '../../hooks/useProductSearch';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { calculateLineTotal, formatCurrency } from '../../utils/orderCalculations';
import { getImageUrl } from '../../utils/imageUtils';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DatePicker from '../../components/ui/DatePicker/DatePicker';
import styles from './CreateQuotationPage.module.css';

let itemIdSeq = 1;

const CreateQuotationPage = () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [shops, setShops] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [shopId, setShopId] = useState('');
  const [customerMode, setCustomerMode] = useState('prospect'); // 'existing' | 'prospect'
  const [customerId, setCustomerId] = useState('');
  const [prospectName, setProspectName] = useState('');
  const [prospectPhone, setProspectPhone] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([]);
  const [errors, setErrors] = useState({});

  const searchInputRef = useRef(null);
  const {
    searchTerm, searchResults, isSearching: isLoadingResults, showResults, hasSearched,
    resultsRef, handleSearchChange, selectProduct, clearSearch, hideResults, showResultsDropdown,
  } = useProductSearch(shopId);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        const [shopsResult, customersResult] = await Promise.all([
          getShops({ assigned: true, type: 'pos' }),
          getCustomers({ limit: 200 }),
        ]);
        const shopList = Array.isArray(shopsResult) ? shopsResult : shopsResult?.shops || [];
        setShops(shopList);
        if (shopList.length === 1) setShopId(shopList[0].id);
        setCustomers(customersResult?.customers || []);
      } catch (err) {
        console.error('Failed to load reference data:', err);
        error(t('quotation.failedToLoadReferenceData'));
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addItem = (product) => {
    setItems(prev => ([
      ...prev,
      {
        id: `new-${itemIdSeq++}`,
        productId: product.productId,
        variationId: product.variationId || null,
        displayName: product.formattedName || product.displayName,
        sku: product.sku,
        image: product.image || null,
        quantity: 1,
        unitPrice: parseFloat(product.price) || 0,
        lineDiscount: 0,
        lineTax: 0,
      },
    ]));
  };

  const updateItem = (itemId, field, value) => {
    setItems(prev => prev.map(item => item.id === itemId ? { ...item, [field]: value } : item));
  };

  const removeItem = (itemId) => {
    setItems(prev => prev.filter(item => item.id !== itemId));
  };

  const handleProductSelect = (product) => {
    const selected = selectProduct(product);
    addItem(selected);
    clearSearch();
    setTimeout(() => searchInputRef.current?.focus(), 100);
  };

  const orderTotal = items.reduce((sum, item) => {
    const calc = calculateLineTotal(item.quantity, item.unitPrice, item.lineDiscount, 0);
    return sum + calc.lineTotal;
  }, 0);

  const validateForm = () => {
    const newErrors = {};
    if (!shopId) newErrors.shopId = t('quotation.shopRequired');
    if (customerMode === 'existing' && !customerId) newErrors.customerId = t('quotation.customerRequired');
    if (customerMode === 'prospect' && !prospectName.trim()) newErrors.prospectName = t('quotation.prospectNameRequired');
    if (items.length === 0) newErrors.items = t('quotation.atLeastOneItemRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('quotation.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        shopId: parseInt(shopId),
        customerId: customerMode === 'existing' && customerId ? parseInt(customerId) : null,
        prospectName: customerMode === 'prospect' ? prospectName.trim() : '',
        prospectPhone: customerMode === 'prospect' ? prospectPhone.trim() : '',
        validUntil: validUntil || null,
        notes: notes.trim() || '',
        items: items.map(item => ({
          productId: item.productId,
          variationId: item.variationId,
          quantity: parseFloat(item.quantity),
          unitPrice: parseFloat(item.unitPrice),
          lineDiscount: parseFloat(item.lineDiscount) || 0,
          lineTax: parseFloat(item.lineTax) || 0,
        })),
      };

      const response = await createQuotation(payload);
      success(t('quotation.quotationCreatedSuccess', { number: response.quotation?.quotationNumber }), {
        title: t('common.success'),
      });
      navigate(`/offline-sales/quotations/${response.quotation.id}`);
    } catch (err) {
      console.error('Quotation creation error:', err);
      error(err.response?.data?.message || t('quotation.failedToCreateQuotation'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.createQuotationPage}>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('common.loading')}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.createQuotationPage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <FileText className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('quotation.createQuotation')}</h1>
              <p>{t('quotation.createQuotationDescription')}</p>
            </div>
          </div>
          <button onClick={() => navigate('/offline-sales/quotations')} className={styles.backButton}>
            <ArrowLeft size={16} />
            {t('quotation.backToQuotations')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.quotationForm}>
        <div className={styles.topColumns}>
          <div className={styles.leftColumn}>
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <Store className={styles.sectionIcon} size={16} />
                <h2>{t('quotation.shop')}</h2>
              </div>
              <div className={styles.sectionBody}>
                <div className={styles.fieldGroup}>
                  <label>{t('quotation.shop')} *</label>
                  <Dropdown
                    value={shopId}
                    onChange={value => setShopId(value || '')}
                    options={shops.map(shop => ({ value: shop.id, label: shop.name }))}
                    placeholder={t('quotation.selectShop')}
                    searchable
                    allowClear={false}
                    className={errors.shopId ? styles.error : ''}
                  />
                  {errors.shopId && <span className={styles.errorText}>{errors.shopId}</span>}
                </div>
              </div>
            </div>

            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <User className={styles.sectionIcon} size={16} />
                <h2>{t('quotation.customer')}</h2>
              </div>
              <div className={styles.sectionBody}>
                <div className={styles.toggleRow}>
                  <button
                    type="button"
                    className={`${styles.toggleButton} ${customerMode === 'prospect' ? styles.toggleActive : ''}`}
                    onClick={() => setCustomerMode('prospect')}
                  >
                    {t('quotation.prospectCustomer')}
                  </button>
                  <button
                    type="button"
                    className={`${styles.toggleButton} ${customerMode === 'existing' ? styles.toggleActive : ''}`}
                    onClick={() => setCustomerMode('existing')}
                  >
                    {t('quotation.existingCustomer')}
                  </button>
                </div>

                {customerMode === 'existing' ? (
                  <div className={styles.fieldGroup}>
                    <label>{t('quotation.customer')} *</label>
                    <Dropdown
                      value={customerId}
                      onChange={value => setCustomerId(value || '')}
                      options={customers.map(c => ({ value: c.id, label: `${c.name} ${c.phone ? `(${c.phone})` : ''}` }))}
                      placeholder={t('quotation.selectCustomer')}
                      searchable
                      className={errors.customerId ? styles.error : ''}
                    />
                    {errors.customerId && <span className={styles.errorText}>{errors.customerId}</span>}
                  </div>
                ) : (
                  <>
                    <div className={styles.fieldGroup}>
                      <label>{t('quotation.prospectName')} *</label>
                      <input
                        type="text"
                        value={prospectName}
                        onChange={(e) => setProspectName(e.target.value)}
                        className={`${styles.input} ${errors.prospectName ? styles.error : ''}`}
                        placeholder={t('quotation.prospectNamePlaceholder')}
                      />
                      {errors.prospectName && <span className={styles.errorText}>{errors.prospectName}</span>}
                    </div>
                    <div className={styles.fieldGroup}>
                      <label>{t('quotation.prospectPhone')}</label>
                      <input
                        type="text"
                        value={prospectPhone}
                        onChange={(e) => setProspectPhone(e.target.value)}
                        className={styles.input}
                        placeholder={t('quotation.prospectPhonePlaceholder')}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <FileText className={styles.sectionIcon} size={16} />
                <h2>{t('quotation.details')}</h2>
              </div>
              <div className={styles.sectionBody}>
                <div className={styles.fieldGroup}>
                  <label>{t('quotation.validUntil')}</label>
                  <DatePicker
                    value={validUntil}
                    onChange={(value) => setValidUntil(value)}
                    placeholder={t('quotation.validUntil')}
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className={styles.fieldGroup}>
                  <label>{t('quotation.notes')}</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className={styles.textarea}
                    placeholder={t('quotation.notesPlaceholder')}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className={styles.rightColumn}>
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <Package className={styles.sectionIcon} size={16} />
                <h2>{t('quotation.items')}</h2>
              </div>
              <div className={styles.sectionBody}>
                {!shopId ? (
                  <div className={styles.noShopMessage}>
                    <AlertCircle size={24} />
                    <p>{t('quotation.selectShopFirst')}</p>
                  </div>
                ) : (
                  <>
                    <div className={styles.searchWrapper}>
                      <div className={styles.searchInputWrapper}>
                        <Search className={styles.searchIcon} size={18} />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchTerm}
                          onChange={(e) => handleSearchChange(e.target.value)}
                          onFocus={showResultsDropdown}
                          onBlur={() => setTimeout(hideResults, 200)}
                          placeholder={t('quotation.searchProductsPlaceholder')}
                          className={styles.searchInput}
                          autoComplete="off"
                        />
                        {searchTerm && (
                          <button type="button" onClick={clearSearch} className={styles.clearSearchButton}>
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      {showResults && (
                        <div className={styles.searchResults} ref={resultsRef}>
                          {isLoadingResults ? (
                            <div className={styles.searchLoading}>{t('common.loading')}</div>
                          ) : searchResults.length > 0 ? (
                            <div className={styles.resultsList}>
                              {searchResults.map((product) => (
                                <div
                                  key={`${product.productId}-${product.variationId || 'simple'}`}
                                  className={styles.resultItem}
                                  onClick={() => handleProductSelect(product)}
                                >
                                  <div className={styles.productInfo}>
                                    <div className={styles.productName}>{product.formattedName}</div>
                                    <span className={styles.productSku}><Hash size={12} />{product.sku || 'No SKU'}</span>
                                  </div>
                                  <div className={styles.productPrice}>{product.formattedPrice}</div>
                                </div>
                              ))}
                            </div>
                          ) : hasSearched ? (
                            <div className={styles.noResults}>
                              <AlertCircle size={16} />
                              <span>{t('quotation.noProductsFound')}</span>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <div className={styles.itemsList}>
                      {items.length === 0 ? (
                        <div className={styles.emptyState}>
                          <Package size={40} />
                          <p>{t('quotation.atLeastOneItemRequired')}</p>
                        </div>
                      ) : items.map((item) => {
                        const lineCalc = calculateLineTotal(item.quantity, item.unitPrice, item.lineDiscount, 0);
                        return (
                          <div key={item.id} className={styles.itemRow}>
                            <div className={styles.itemHeader}>
                              <div className={ps.productCellInner}>
                                <ProductThumb src={getImageUrl(item.image)} alt={item.displayName || ''} />
                                <div className={ps.productCell}>
                                  <span className={styles.itemName}>{item.displayName}</span>
                                  {item.sku && <span className={ps.productSku}>SKU: {item.sku}</span>}
                                </div>
                              </div>
                              <button type="button" onClick={() => removeItem(item.id)} className={styles.removeItemButton}>
                                <X size={16} />
                              </button>
                            </div>
                            <div className={styles.itemFields}>
                              <div className={styles.fieldGroup}>
                                <label>{t('quotation.quantity')}</label>
                                <div className={styles.quantityWrapper}>
                                  <button
                                    type="button"
                                    onClick={() => updateItem(item.id, 'quantity', Math.max(1, item.quantity - 1))}
                                    className={styles.quantityButton}
                                  >
                                    <Minus size={12} />
                                  </button>
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    min="1"
                                    onChange={(e) => updateItem(item.id, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                                    className={styles.quantityInput}
                                  />
                                  <button
                                    type="button"
                                    onClick={() => updateItem(item.id, 'quantity', item.quantity + 1)}
                                    className={styles.quantityButton}
                                  >
                                    <Plus size={12} />
                                  </button>
                                </div>
                              </div>
                              <div className={styles.fieldGroup}>
                                <label>{t('quotation.unitPrice')}</label>
                                <input
                                  type="number"
                                  value={item.unitPrice}
                                  min="0"
                                  step="0.01"
                                  onChange={(e) => updateItem(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                                  className={styles.priceInput}
                                />
                              </div>
                              <div className={styles.fieldGroup}>
                                <label>{t('quotation.lineDiscount')}</label>
                                <input
                                  type="number"
                                  value={item.lineDiscount}
                                  min="0"
                                  step="0.01"
                                  onChange={(e) => updateItem(item.id, 'lineDiscount', parseFloat(e.target.value) || 0)}
                                  className={styles.priceInput}
                                />
                              </div>
                              <div className={styles.fieldGroup}>
                                <label>{t('quotation.lineTotal')}</label>
                                <div className={styles.lineTotalDisplay}>{formatCurrency(lineCalc.lineTotal)}</div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {errors.items && (
                        <div className={styles.sectionError}><AlertCircle size={16} />{errors.items}</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.actionBarSpacer} />
      </form>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            {orderTotal > 0 && (
              <span className={styles.actionBarTotal}>
                {t('quotation.total')}: <strong>{formatCurrency(orderTotal)}</strong>
              </span>
            )}
            {items.length > 0 && (
              <span className={styles.actionBarItemCount}>
                {items.length} {t('quotation.itemsLabel')}
              </span>
            )}
          </div>
          <div className={styles.actionBarButtons}>
            <button type="button" onClick={() => navigate('/offline-sales/quotations')} className={styles.cancelButton} disabled={isSubmitting}>
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting}>
              {isSubmitting ? <div className={styles.loadingSpinner} /> : (
                <>
                  <Save size={15} />
                  {t('quotation.createQuotation')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateQuotationPage;
